import { useState, useEffect, useRef } from "react";
import { Code } from "lucide-react";
export default function AIWebsiteFeatures() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);
  return <section ref={sectionRef} className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    backgroundColor: "rgb(255, 255, 255)",
    backgroundPosition: "0% 0%",
    backgroundRepeat: "repeat",
    color: "rgb(20, 21, 31)",
    fontFamily: '"Evergrow Sans", sans-serif',
    animation: "none 0s ease 0s 1 normal none running"
  }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap');

        @keyframes cursor {
          0% { opacity: 0; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }

        .opened-cards-section {
          padding-bottom: 64px;
        }

        .feature-card {
          background-color: #ffffff;
          border: 1px solid #E5E7EB;
          border-radius: 20px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          padding: 32px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          word-break: break-word;
        }

        .feature-card h3, .feature-card p {
          overflow-wrap: break-word;
        }

        .feature-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          transition: box-shadow 0.3s ease;
        }

        .card-image {
          border-radius: 12px;
          width: 100%;
          height: auto;
          display: block;
          margin-top: 16px;
        }

        .show-after-visible {
          transition-property: all;
          transition-duration: 0.5s;
          transition-timing-function: cubic-bezier(0.12, 0.23, 0.5, 1);
          opacity: 0;
        }

        .show-after-visible.visible {
          opacity: 1;
          transform: translate(0, 0);
        }

        .show-after-visible.from-bottom {
          transform: translate(0px, 30px);
        }

        .show-after-visible.td0 { transition-delay: 0.09s; }
        .show-after-visible.td1 { transition-delay: 0.1s; }
        .show-after-visible.td2 { transition-delay: 0.2s; }
        .show-after-visible.td3 { transition-delay: 0.3s; }
        .show-after-visible.td4 { transition-delay: 0.4s; }
        .show-after-visible.td5 { transition-delay: 0.5s; }

        @media (max-width: 767px) {
          .opened-cards-section {
            padding-top: 72px !important;
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          .opened-cards-section h2 {
            font-size: clamp(28px, 6vw, 40px) !important;
            line-height: 1.2 !important;
          }
          .feature-card {
            padding: 24px !important;
            min-height: auto !important;
            overflow: visible !important;
          }
          .feature-card h3 {
            font-size: clamp(20px, 5vw, 28px) !important;
            line-height: 1.3 !important;
          }
          .mobile-gap-fix {
            gap: 16px !important;
          }
        }

        @media (min-width: 768px) {
          .opened-cards-section {
            padding-top: 96px !important;
          }
        }
      `}</style>

      <div className="opened-cards-section" style={{
      backgroundColor: "rgb(255, 255, 255)",
      backgroundPosition: "0% 0%",
      backgroundRepeat: "repeat",
      color: "rgb(20, 21, 31)",
      fontFamily: '"Evergrow Sans", sans-serif',
      animation: "none 0s ease 0s 1 normal none running"
    }}>
        <div className="page-container">
          <div className={`show-after-visible from-bottom td0 ${visible ? "visible" : ""}`}>
            <h2 style={{
            color: "rgb(20, 21, 31)",
            fontFamily: '"Evergrow Sans", "Inter", sans-serif',
            fontSize: "40px",
            fontWeight: "700",
            lineHeight: "52px",
            border: "0px none rgb(20, 21, 31)",
            animation: "none 0s ease 0s 1 normal none running",
            marginBottom: "16px"
          }} className="!font-medium">Todo lo que contratarías a un equipo web... integrado.</h2>
            <p style={{
            color: "rgb(20, 21, 31)",
            fontFamily: '"Evergrow Sans", "Inter", sans-serif',
            fontSize: "18px",
            fontWeight: "400",
            lineHeight: "28px",
            border: "0px none rgb(20, 21, 31)",
            animation: "none 0s ease 0s 1 normal none running",
            maxWidth: "760px",
            marginBottom: "60px"
          }}>Cynergia reemplaza a la agencia, al diseñador y al desarrollador. Un prompt construye el sitio. Un chat lo mantiene actualizado. Una plataforma lo aloja todo.</p>
          </div>

          <div className="flex flex-col md:flex-row items-start mobile-gap-fix" style={{
          gap: "40px"
        }}>
            <div className={`feature-card show-after-visible from-bottom td1 ${visible ? "visible" : ""}`} style={{
            flex: "1 1 0",
            minWidth: 0
          }}>
              <div style={{
              marginBottom: "24px"
            }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="rgb(20, 21, 31)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="rgb(20, 21, 31)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "28px",
              fontWeight: "500",
              lineHeight: "40px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running"
            }}>
                Vibe Coding para sitios web.
              </h3>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/vibe-coding-for-websites_1x.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/vibe-coding-for-websites_1x.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/vibe-coding-for-websites_1x.jpg-wvc-sizes" alt="Vibe Coding para sitios web." className="card-image" loading="lazy" />
            </div>

            <div className={`feature-card show-after-visible from-bottom td2 ${visible ? "visible" : ""}`} style={{
            flex: "1 1 0",
            minWidth: 0
          }}>
              <div style={{
              marginBottom: "16px"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/cloning.svg" alt="Icon representing URL-to-site cloning." style={{
                width: "40px",
                height: "40px"
              }} />
              </div>
              <h3 style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "28px",
              fontWeight: "500",
              lineHeight: "40px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running",
              marginBottom: "16px"
            }}>
                Clonación de URL a sitio.
              </h3>
              <p style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "18px",
              fontWeight: "400",
              lineHeight: "28px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running",
              marginBottom: "32px"
            }}>Recrea y rediseña cualquier sitio web existente a partir de una URL. Cynergia AI clonará un sitio editable sin reconstrucción manual.</p>
            </div>

            <div className={`feature-card show-after-visible from-bottom td3 ${visible ? "visible" : ""}`} style={{
            flex: "1 1 0",
            minWidth: 0
          }}>
              <h3 style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "28px",
              fontWeight: "500",
              lineHeight: "40px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running",
              marginBottom: "0"
            }}>
                Ruta de Ecommerce escalable.
              </h3>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/scalable-ecommerce-path_1x.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/scalable-ecommerce-path_1x.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/scalable-ecommerce-path_1x.jpg-wvc-sizes" alt="Sitio web de alquiler vacacional con soporte multilingüe." className="card-image" loading="lazy" />
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-start mobile-gap-fix" style={{
          gap: "40px"
        }}>
            <div className={`feature-card show-after-visible from-bottom td3 ${visible ? "visible" : ""}`} style={{
            flex: "1 1 0",
            minWidth: 0
          }}>
              <div style={{
              marginBottom: "16px"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/editor.svg" alt="Icon representing Unified smart editor." style={{
                width: "40px",
                height: "40px"
              }} />
              </div>
              <h3 style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "28px",
              fontWeight: "500",
              lineHeight: "40px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running",
              marginBottom: "16px"
            }}>
                Editor inteligente unificado.
              </h3>
              <p style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "18px",
              fontWeight: "400",
              lineHeight: "28px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running"
            }}>Edita tu sitio de tres maneras en la misma ventana: chatea con la IA, haz clic para editar cualquier elemento o escribe código directamente. Lo que sea más rápido para la tarea en cuestión.</p>
            </div>

            <div className={`feature-card show-after-visible from-bottom td4 ${visible ? "visible" : ""}`} style={{
            flex: "1 1 0",
            minWidth: 0
          }}>
              <h3 style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "28px",
              fontWeight: "500",
              lineHeight: "40px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running",
              marginBottom: "16px"
            }}>
                De Figma a sitio web en vivo.
              </h3>
              <p style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "18px",
              fontWeight: "400",
              lineHeight: "28px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running"
            }}>Importa tus diseños de Figma directamente al constructor de Cynergia para convertirlos en un sitio de WordPress totalmente funcional y responsivo.</p>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/figma-to-live-website_1x.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/figma-to-live-website_1x.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/figma-to-live-website_1x.jpg-wvc-sizes" alt="Interfaz que muestra el panel de gestión multisitio." className="card-image" loading="lazy" />
            </div>

            <div className={`feature-card show-after-visible from-bottom td5 ${visible ? "visible" : ""}`} style={{
            flex: "1 1 0",
            minWidth: 0
          }}>
              <div style={{
              marginBottom: "16px"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683000/img/content-engine.svg" alt="Icono que representa el motor de contenido dinámico." style={{
                width: "40px",
                height: "40px"
              }} />
              </div>
              <h3 style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "28px",
              fontWeight: "500",
              lineHeight: "40px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running",
              marginBottom: "16px"
            }}>
                Motor de contenido dinámico.
              </h3>
              <p style={{
              color: "rgb(20, 21, 31)",
              fontFamily: '"Evergrow Sans", "Inter", sans-serif',
              fontSize: "18px",
              fontWeight: "400",
              lineHeight: "28px",
              border: "0px none rgb(20, 21, 31)",
              animation: "none 0s ease 0s 1 normal none running"
            }}>Formularios de contacto, reservas en línea, calendarios de citas, captación de leads — funciones prácticas que tus clientes realmente usan, integradas.</p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}